import { useStt, useTts, useAvatarState, ShortCodeService } from '@ht/lib-plural-vue';
import types from '@/actions/types';
import useConfig from '@/composables/useConfig';
import useContent from '@/composables/useContent';
import Base from '@/actions/base';
import useAnalytics from '@/composables/useAnalytics';
import { SYSTEM_VARIABLES_MAP } from '@/js/constants';

export default class Say extends Base {
  constructor(params) {
    super(params, types.SAY);

    this.stopped = false;
    this.audio = null;
    this.audioProcessor = null;
    this.animationTimeout = null;
    this.avatarAnimationDuration = 0;
  }

  execute() {
    const { trackAction, lastAnalyticData } = useAnalytics();
    trackAction(this);

    // eslint-disable-next-line no-async-promise-executor
    this.promise = new Promise(async (resolve) => {
      const { sttStart, sttStop } = useStt();
      const { isContentSupportStt, setSubtitle } = useContent();
      this.resolve = resolve;
      const { initialConfig, currentConfig, currentLanguage, isPreview, getVariable } = useConfig();
      const {
        setTalking,
      } = useAvatarState();
      const {
        preFetchAudioContent,
        createAudioDataItem,
        setAudioSrcAndCallbacks,
        processUtterance,
      } = useTts();

      const utterance = new ShortCodeService(this.params.utterance).matchAttributes().getValue();
      if (!utterance) {
        resolve();
        return;
      }

      let speechMapKey = null;
      if (this.params.forceReuse) {
        const processedUtteranceResult = processUtterance({
          utterance: this.params.utterance,
          currentLanguage,
        });
        speechMapKey = processedUtteranceResult.speechMapKey;
      } else {
        sttStop();
        speechMapKey = await preFetchAudioContent({
          utterance: this.params.utterance,
          currentLanguage,
          config: initialConfig.value,
          meta: {
            projectId: currentConfig.value.projectId,
            sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
            isPreview: isPreview(),
            elementId: lastAnalyticData.value?.elementId,
          },
        });
      }

      const [, value] = speechMapKey.split('_');
      if (!value) {
        if (isContentSupportStt()) {
          sttStart({
            sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
            elementId: lastAnalyticData.value.elementId,
          });
        }
        resolve();
        return;
      }

      const {
        audio,
        audioProcessor,
      } = createAudioDataItem(speechMapKey);

      this.audio = audio;
      this.audioProcessor = audioProcessor;

      if (!this.stopped && audio) {
        const self = this;
        setAudioSrcAndCallbacks({
          speechMapKey,
          callbacks: {
            onPlay() {},
            onPlaying(avatarAnimationDuration) {
              if (!self.stopped) {
                setTalking(true);
                self.avatarAnimationDuration = avatarAnimationDuration;
                self.startAnimationWithTimeout();
              }
            },
            onTimeUpdate(text) {
              if (!self.stopped && text && currentConfig.value.ttsSubtitles) {
                setSubtitle(text);
              }
            },
            onAudioProcess(talking) {
              if (!self.stopped) {
                setTalking(talking);
              }
            },
            onEnded() {
              if (!self.stopped) {
                setSubtitle('');
                if (isContentSupportStt()) {
                  sttStart({
                    sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
                    elementId: lastAnalyticData.value.elementId,
                  });
                }
                setTalking(false);
                clearTimeout(self.animationTimeout);
                resolve();
              }
            },
          },
        });

        audio.play()
          .catch(() => {
            console.log('auto play blocked'); // eslint-disable-line
            // resolving to not block with action
            resolve();
          });
      } else {
        resolve();
      }
    });

    return this.promise;
  }

  stop() {
    const { setSubtitle } = useContent();
    const { setTalking } = useAvatarState();

    if (this.audioProcessor) {
      this.audioProcessor.onaudioprocess = undefined;
    }

    setTalking(false);
    setSubtitle('');
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }

    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }

    this.stopped = true;
    this.resolve();
  }

  startAnimationWithTimeout() {
    const { setTalking } = useAvatarState();
    clearTimeout(this.animationTimeout);
    this.animationTimeout = setTimeout(() => {
      setTalking(false);
    }, this.avatarAnimationDuration);
  }

  pause() {
    if (this.audio) {
      this.audio.pause();
      clearTimeout(this.animationTimeout);
    }
  }

  resume() {
    if (this.audio) {
      this.audio.play();
      this.startAnimationWithTimeout();
    }
  }
}
